import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { AppHeader } from '@/components/AppHeader';
import { 
  Plus, 
  Search, 
  Users, 
  Calendar, 
  Trophy, 
  Lock, 
  Globe,
  Star,
  BookOpen,
  TrendingUp,
  Filter
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface League {
  id: string;
  name: string;
  owner_id: string;
  is_private: boolean;
  password_hash?: string;
  draft_at?: string;
  starters_count: number;
  bench_count: number;
  max_teams: number;
  created_at: string;
  updated_at: string;
  participants_count?: number;
  creator_name?: string;
}

const League = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [leagues, setLeagues] = useState<League[]>([]);
  const [featuredLeagues, setFeaturedLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [privacyFilter, setPrivacyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [passwordDialog, setPasswordDialog] = useState<{ open: boolean; leagueId: string }>({ open: false, leagueId: '' });
  const [password, setPassword] = useState('');

  useEffect(() => {
    fetchLeagues();
    fetchFeaturedLeagues();
    
    // Set up real-time subscription for league participants
    const channel = supabase
      .channel('league-participants-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_players'
        },
        () => {
          // Refresh leagues when participants change
          fetchLeagues();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_teams'
        },
        () => {
          // Refresh leagues when bot teams change
          fetchLeagues();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [searchQuery, statusFilter, sortBy]);

  // Set up real-time updates for league participants
  useEffect(() => {
    const channel = supabase
      .channel('league-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_players'
        },
        (payload) => {
          console.log('League player change:', payload);
          // Refresh leagues when participants change
          fetchLeagues();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchLeagues = async () => {
    try {
      const { data: leaguesData, error } = await supabase
        .from('leagues')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get participant counts and creator names for each league
      const leaguesWithCounts = await Promise.all(
        (leaguesData || []).map(async (league) => {
          // Get participant count
          const { count } = await supabase
            .from('league_players')
            .select('*', { count: 'exact', head: true })
            .eq('league_id', league.id);

          // Get creator name
          const { data: creatorProfile } = await supabase
            .from('profiles')
            .select('username, display_name')
            .eq('user_id', league.owner_id)
            .maybeSingle();

          return {
            ...league,
            participants_count: count || 0,
            creator_name: creatorProfile?.display_name || creatorProfile?.username || 'Unknown',
          };
        })
      );

      // Apply client-side filtering
      let filteredLeagues = leaguesWithCounts;

      // Status filtering removed as new schema doesn't have status field

      if (searchQuery) {
        filteredLeagues = filteredLeagues.filter(league => 
          league.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      // Apply sorting
      switch (sortBy) {
        case 'active':
          filteredLeagues.sort((a, b) => (b.participants_count || 0) - (a.participants_count || 0));
          break;
        case 'starting':
          filteredLeagues.sort((a, b) => {
            const dateA = a.draft_at ? new Date(a.draft_at).getTime() : 0;
            const dateB = b.draft_at ? new Date(b.draft_at).getTime() : 0;
            return dateA - dateB;
          });
          break;
        default:
          filteredLeagues.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }

      setLeagues(filteredLeagues);
    } catch (error) {
      console.error('Error fetching leagues:', error);
      toast({
        title: 'Error loading leagues',
        description: 'Please try refreshing the page.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedLeagues = async () => {
    try {
      const { data: featuredData, error } = await supabase
        .from('leagues')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;

      const featuredWithCounts = await Promise.all(
        (featuredData || []).map(async (league) => {
          // Get participant count
          const { count } = await supabase
            .from('league_participants')
            .select('*', { count: 'exact', head: true })
            .eq('league_id', league.id);

          // Get creator name
          const { data: creatorProfile } = await supabase
            .from('profiles')
            .select('username, display_name')
            .eq('user_id', league.owner_id)
            .maybeSingle();

          return {
            ...league,
            participants_count: count || 0,
            creator_name: creatorProfile?.display_name || creatorProfile?.username || 'Unknown',
          };
        })
      );

      setFeaturedLeagues(featuredWithCounts);
    } catch (error) {
      console.error('Error fetching featured leagues:', error);
    }
  };

  const joinLeague = async (leagueId: string, requiresPassword = false) => {
    if (!user) return;

    try {
      // First check if the league allows new participants
      const { data: leagueData, error: leagueError } = await supabase
        .from('leagues')
        .select('status, max_teams')
        .eq('id', leagueId)
        .single();

      if (leagueError) throw leagueError;

      // Prevent joining if draft has started
      if (leagueData.status === 'live' || leagueData.status === 'completed') {
        toast({
          title: 'Cannot join league',
          description: 'This league has already started or completed.',
          variant: 'destructive',
        });
        return;
      }

      // Check if league is full
      const { count } = await supabase
        .from('league_players')
        .select('*', { count: 'exact', head: true })
        .eq('league_id', leagueId);

      if (count && count >= leagueData.max_teams) {
        toast({
          title: 'League is full',
          description: 'This league has reached its maximum number of participants.',
          variant: 'destructive',
        });
        return;
      }

      const { error } = await supabase
        .from('league_players')
        .insert({
          league_id: leagueId,
          user_id: user.id,
        });

      if (error) throw error;

      toast({
        title: 'Successfully joined league!',
        description: 'Welcome to the league',
      });

      // Refresh leagues
      fetchLeagues();
      setPasswordDialog({ open: false, leagueId: '' });
      setPassword('');

    } catch (error: any) {
      console.error('Error joining league:', error);
      toast({
        title: 'Failed to join league',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays > 0) return `In ${diffDays} days`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'upcoming':
        return <Badge variant="secondary">Upcoming</Badge>;
      case 'active':
        return <Badge variant="default">Ongoing</Badge>;
      case 'completed':
        return <Badge variant="outline">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Trophy className="h-12 w-12 mx-auto text-primary animate-pulse mb-4" />
          <p className="text-muted-foreground">Loading leagues...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto px-4 py-6">
            <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1 space-y-6">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold mb-2">League Lobby</h1>
              <p className="text-muted-foreground">Browse and join leagues to compete with other players</p>
            </div>

            {/* Create New League Card */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 hover:shadow-lg transition-all duration-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Plus className="h-6 w-6 text-primary" />
                  </div>
                  Create New League
                </CardTitle>
                <CardDescription className="text-base">
                  Start your own league and invite friends to compete
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  size="lg" 
                  className="bg-primary hover:bg-primary/90 h-12 px-8 font-semibold"
                  onClick={() => navigate('/leagues/create')}
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create League
                </Button>
              </CardContent>
            </Card>

            {/* Search & Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search leagues by name or owner..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Filter Row */}
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Filters:</span>
                    </div>

                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="active">Ongoing</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">Newest</SelectItem>
                        <SelectItem value="active">Most Active</SelectItem>
                        <SelectItem value="starting">Starting Soon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* League Directory */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">Available Leagues</h2>
              
              {leagues.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Trophy className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No leagues found</h3>
                    <p className="text-muted-foreground mb-4">
                      Try another search or create your own league
                    </p>
                    <Button onClick={() => navigate('/leagues/create')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create League
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {leagues.map((league) => (
                    <Card 
                      key={league.id} 
                      className="hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer group"
                      onClick={() => navigate(`/league/${league.id}`)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg truncate group-hover:text-primary transition-colors">
                              {league.name}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">by {league.creator_name}</p>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            {league.is_private ? (
                              <Lock className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Globe className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>{league.participants_count}/{league.max_teams}</span>
                          </div>
                          <Badge variant="outline">Active</Badge>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>
                            Draft: {league.draft_at ? new Date(league.draft_at).toLocaleDateString() : 'TBD'}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Trophy className="h-4 w-4" />
                          <span>{league.starters_count}+{league.bench_count} roster</span>
                        </div>

                        {/* Action Button - Revealed on Hover */}
                        <div className="pt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            {league.is_private ? (
                            <Dialog 
                              open={passwordDialog.open && passwordDialog.leagueId === league.id}
                              onOpenChange={(open) => setPasswordDialog({ open, leagueId: open ? league.id : '' })}
                            >
                              <DialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="w-full"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Lock className="h-4 w-4 mr-2" />
                                  Enter Password
                                </Button>
                              </DialogTrigger>
                              <DialogContent onClick={(e) => e.stopPropagation()}>
                                <DialogHeader>
                                  <DialogTitle>Join Private League</DialogTitle>
                                  <DialogDescription>
                                    Enter the password to join "{league.name}"
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <Input
                                    type="password"
                                    placeholder="Enter league password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                  />
                                  <div className="flex gap-2">
                                    <Button 
                                      variant="outline" 
                                      onClick={() => {
                                        setPasswordDialog({ open: false, leagueId: '' });
                                        setPassword('');
                                      }}
                                      className="flex-1"
                                    >
                                      Cancel
                                    </Button>
                                    <Button 
                                      onClick={() => joinLeague(league.id, true)}
                                      className="flex-1"
                                    >
                                      Join League
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          ) : (
                            <Button 
                              variant="default" 
                              size="sm" 
                              className="w-full"
                              onClick={(e) => {
                                e.stopPropagation();
                                joinLeague(league.id);
                              }}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Join League
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-80 space-y-6">
            {/* Featured Leagues */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Featured Leagues
                </CardTitle>
                <CardDescription>
                  Top recommended leagues
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {featuredLeagues.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No featured leagues yet
                  </p>
                ) : (
                  featuredLeagues.map((league) => (
                    <Card 
                      key={league.id} 
                      className="p-3 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/league/${league.id}`)}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-sm truncate">{league.name}</h4>
                          <Badge variant="outline">Featured</Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{league.participants_count}/12 teams</span>
                          <span>{league.starters_count}+{league.bench_count} roster</span>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Tips & Guides */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  Tips & Guides
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start h-auto p-3 text-left"
                    onClick={() => {/* Add navigation to guide */}}
                  >
                    <TrendingUp className="h-4 w-4 mr-3 text-primary" />
                    <div>
                      <div className="font-medium text-sm">How to start your first league</div>
                      <div className="text-xs text-muted-foreground">Complete beginner's guide</div>
                    </div>
                  </Button>

                  <Button 
                    variant="ghost" 
                    className="w-full justify-start h-auto p-3 text-left"
                    onClick={() => {/* Add navigation to guide */}}
                  >
                    <Trophy className="h-4 w-4 mr-3 text-primary" />
                    <div>
                      <div className="font-medium text-sm">Winning strategies</div>
                      <div className="text-xs text-muted-foreground">Tips from top players</div>
                    </div>
                  </Button>

                  <Button 
                    variant="ghost" 
                    className="w-full justify-start h-auto p-3 text-left"
                    onClick={() => {/* Add navigation to guide */}}
                  >
                    <Users className="h-4 w-4 mr-3 text-primary" />
                    <div>
                      <div className="font-medium text-sm">League management</div>
                      <div className="text-xs text-muted-foreground">Best practices for admins</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
            </div>
          </div>
        </div>
      </div>
    </SidebarInset>
  </SidebarProvider>
);
};

export default League;